import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleBidsService } from 'src/app/services/bids/vehicle-bids.service';
import { Bid } from 'src/app/shared/models/bid.interface';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-vh-update-bid',
  templateUrl: './vh-update-bid.component.html',
  styleUrls: ['./vh-update-bid.component.scss']
})
export class VhUpdateBidComponent extends DestructorComponent implements OnInit {

  @Output() bidUpdated = new EventEmitter<void>();

  bidForm: FormGroup;
  isLoading: boolean = false;
  selectedBid: Bid | null = null;
  isFormDisabled: boolean = false;

  // Dropdown options
  bidStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'purchased', label: 'Purchased' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  groupOptions = [
    { value: 'No Group', label: 'No Group' },
    { value: 'A', label: 'Group A' },
    { value: 'B', label: 'Group B' },
    { value: 'C', label: 'Group C' },
    { value: 'D', label: 'Group D' }
  ];

  unitOptions = [
    { value: 'No', label: 'No' },
    { value: 'Yes', label: 'Yes' }
  ];

  constructor(
    private fb: FormBuilder,
    private _vehicleBidsService: VehicleBidsService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.bidForm = this.fb.group({
      auction_hall: [''],
      lot_no: ['', Validators.required],
      destination_country: [''],
      destination_port: [''],
      bid_offer_price: [0],
      price_type: [''],
      price_type_amount: [0],
      bid_status: ['pending', Validators.required],
      bid_finish_price: [0, [Validators.required, Validators.min(0)]],
      group: ['No Group'],
      unit: ['No'],
      admin_comment: ['', [Validators.maxLength(500)]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_update_bid');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        if (this.selectedBid) {
          this.populateForm(this.selectedBid);
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
      });
    }
  }

  openModal(bid: Bid): void {
    this.selectedBid = bid;
    const modalElement = document.getElementById('kt_modal_update_bid');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  private populateForm(bid: Bid): void {
    // Get unit display text
    const unitDisplayText = this.getUnitDisplayText(bid);

    this.bidForm.patchValue({
      auction_hall: bid.car.hall_name || '',
      lot_no: bid.car.lot_no || '',
      destination_country: bid.to_country?.name || '',
      destination_port: bid.to_port?.name || '',
      bid_offer_price: bid.bid_amount || 0,
      price_type: bid.price_type || '',
      price_type_amount: bid.price_type_amount || 0,
      bid_status: bid.status || 'pending',
      bid_finish_price: bid.result_amount || bid.bid_amount || 0,
      group: bid.group || 'No Group',
      unit: unitDisplayText,
      admin_comment: '' // Admin comment starts empty, separate from bid message
    });

    // Check if form should be disabled based on bid status
    this.isFormDisabled = bid.status !== 'pending';
    this.updateFormState();
  }

  private updateFormState(): void {
    const editableFields = ['bid_status', 'bid_finish_price', 'admin_comment'];

    if (this.isFormDisabled) {
      editableFields.forEach(field => {
        this.bidForm.get(field)?.disable();
      });
    } else {
      editableFields.forEach(field => {
        this.bidForm.get(field)?.enable();
      });
    }

    // These fields are always disabled (read-only)
    this.bidForm.get('unit')?.disable();
    this.bidForm.get('group')?.disable();
    this.bidForm.get('price_type')?.disable();
    this.bidForm.get('price_type_amount')?.disable();
    this.bidForm.get('destination_country')?.disable();
    this.bidForm.get('destination_port')?.disable();
  }

  // Helper method to get unit display text
  getUnitDisplayText(bid: Bid): string {
    if (bid.bid_unit && bid.bid_unit.number_of_units_bought !== undefined && bid.bid_unit.number_of_units !== undefined) {
      return `${bid.bid_unit.number_of_units_bought} / ${bid.bid_unit.number_of_units}`;
    }
    return 'N/A';
  }

  private resetForm(): void {
    this.bidForm.reset({
      auction_hall: '',
      lot_no: '',
      destination_country: '',
      destination_port: '',
      bid_offer_price: 0,
      price_type: '',
      price_type_amount: 0,
      bid_status: 'pending',
      bid_finish_price: 0,
      group: 'No Group',
      unit: 'No',
      admin_comment: ''
    });
    this.selectedBid = null;
    this.isLoading = false;
    this.isFormDisabled = false;

    // Re-enable all form controls when resetting
    this.updateFormState();
  }

  onSubmit(): void {
    if (this.bidForm.valid && this.selectedBid && !this.isFormDisabled) {
      this.isLoading = true;

      const formData = this.bidForm.value;
      const updateData = {
        status: formData.bid_status,
        result_amount: formData.bid_finish_price,
        admin_comment: formData.admin_comment,
        group: formData.group,
        unit: formData.unit
      };

      this.subscriptions.push(
        this._vehicleBidsService.UpdateData(this.selectedBid._id, updateData).subscribe({
          next: (response) => {
            this.isLoading = false;
            if (response.status) {
              Swal.fire({
                title: 'Success!',
                text: 'Bid updated successfully',
                icon: 'success',
                confirmButtonText: 'OK',
                buttonsStyling: false,
                customClass: {
                  confirmButton: 'btn fw-bold btn-primary'
                }
              }).then(() => {
                this.closeModal();
                this.bidUpdated.emit();
              });
            } else {
              this.showError('Failed to update bid');
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error updating bid:', error);
            if (error.error && error.error.message) {
              this.showError(error.error.message);
            } else if (error.message) {
              this.showError(error.message);
            }
            // this.showError('An error occurred while updating the bid');
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.bidForm);
    }
  }

  private showError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary'
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_update_bid');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Getter methods for form validation
  get auctionHall() { return this.bidForm.get('auction_hall'); }
  get lotNo() { return this.bidForm.get('lot_no'); }
  get destinationCountry() { return this.bidForm.get('destination_country'); }
  get destinationPort() { return this.bidForm.get('destination_port'); }
  get bidOfferPrice() { return this.bidForm.get('bid_offer_price'); }
  get priceType() { return this.bidForm.get('price_type'); }
  get priceTypeAmount() { return this.bidForm.get('price_type_amount'); }
  get bidStatus() { return this.bidForm.get('bid_status'); }
  get bidFinishPrice() { return this.bidForm.get('bid_finish_price'); }
  get group() { return this.bidForm.get('group'); }
  get unit() { return this.bidForm.get('unit'); }
  get adminComment() { return this.bidForm.get('admin_comment'); }
}
